import { useState, useEffect, useMemo } from 'react';
import { getStructuredNotes } from '../api/api';
import { FaFlask, FaAtom, FaCalculator, FaMicroscope, FaBook, FaGraduationCap, FaDownload, FaLock, FaUnlock, FaSearch, FaTimes, FaChevronDown, FaInbox } from 'react-icons/fa';

// Subject icon mapping
const SUBJECT_ICONS = {
  'Physics': <FaFlask size={18} />,
  'Chemistry': <FaAtom size={18} />,
  'Maths': <FaCalculator size={18} />,
  'Mathematics': <FaCalculator size={18} />,
  'Biology': <FaMicroscope size={18} />,
};

// Subject color palette — purely visual, keys match whatever subject names come from backend
const SUBJECT_PALETTE = [
  { bg: '#EFF6FF', border: '#BFDBFE', accent: '#1E3A8A' },
  { bg: '#FFF7ED', border: '#FED7AA', accent: '#C2410C' },
  { bg: '#F0FDF4', border: '#BBF7D0', accent: '#15803D' },
  { bg: '#FDF4FF', border: '#E9D5FF', accent: '#7E22CE' },
  { bg: '#FFFBEB', border: '#FDE68A', accent: '#B45309' },
  { bg: '#F0F9FF', border: '#BAE6FD', accent: '#0369A1' },
];

// Assign a consistent color to each subject name dynamically
const subjectColorCache = {};
let colorIndex = 0;
function getSubjectStyle(subject) {
  if (!subjectColorCache[subject]) {
    subjectColorCache[subject] = SUBJECT_PALETTE[colorIndex % SUBJECT_PALETTE.length];
    colorIndex++;
  }
  return subjectColorCache[subject];
}

// ── Chapter Row ───────────────────────────────────────────────
function ChapterRow({ chapter, index, total, isActive, onActivate, onPrev, onNext }) {
  const url = chapter.fileUrl && chapter.fileUrl !== '#'
    ? (chapter.fileUrl.startsWith('http') ? chapter.fileUrl : `https://sbs-coaching-backend-production.up.railway.app${chapter.fileUrl}`)
    : null;

  return (
    <div style={{
      border: `1px solid ${isActive ? 'var(--blue-light)' : 'var(--border)'}`,
      borderRadius: 10,
      background: isActive ? 'var(--blue-pale)' : 'var(--white)',
      transition: 'all 0.2s',
      overflow: 'hidden',
    }}>
      <div
        onClick={onActivate}
        style={{
          display: 'flex', alignItems: 'center',
          gap: '1rem', padding: '0.9rem 1.2rem',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        {/* Chapter number badge */}
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: isActive ? 'var(--blue)' : 'var(--bg)',
          border: `2px solid ${isActive ? 'var(--blue)' : 'var(--border)'}`,
          color: isActive ? '#fff' : 'var(--gray)',
          fontWeight: 800, fontSize: '0.78rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'all 0.2s',
        }}>
          {chapter.chapterNumber ?? index + 1}
        </div>

        {/* Title */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 600, fontSize: '0.92rem',
            color: isActive ? 'var(--blue)' : 'var(--dark)',
          }}>
            Chapter {chapter.chapterNumber ?? index + 1}: {chapter.title}
          </div>
        </div>

        {/* Action button */}
        {url ? (
          <a
            href={url}
            download
            onClick={e => e.stopPropagation()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
              background: 'var(--blue)', color: '#fff',
              padding: '0.4rem 1rem', borderRadius: 6,
              fontWeight: 600, fontSize: '0.8rem',
              textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-mid)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--blue)'}
          >
            <FaDownload size={12} /> Download
          </a>
        ) : (
          <span style={{
            background: 'var(--gray-light)', color: 'var(--gray)',
            padding: '0.4rem 1rem', borderRadius: 6,
            fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            Coming Soon
          </span>
        )}
      </div>

      {/* Prev / Next nav — shown when chapter is active */}
      {isActive && (
        <div style={{
          borderTop: '1px solid #BFDBFE',
          padding: '0.65rem 1.2rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#EFF6FF',
        }}>
          <button
            onClick={onPrev} disabled={index === 0}
            style={{
              background: 'none', border: '1px solid var(--border)',
              borderRadius: 6, padding: '0.3rem 0.85rem',
              color: index === 0 ? 'var(--gray-light)' : 'var(--blue)',
              fontWeight: 600, fontSize: '0.78rem',
              cursor: index === 0 ? 'not-allowed' : 'pointer',
            }}
          >← Prev</button>
          <span style={{ color: 'var(--gray)', fontSize: '0.75rem' }}>
            {index + 1} / {total}
          </span>
          <button
            onClick={onNext} disabled={index === total - 1}
            style={{
              background: 'none', border: '1px solid var(--border)',
              borderRadius: 6, padding: '0.3rem 0.85rem',
              color: index === total - 1 ? 'var(--gray-light)' : 'var(--blue)',
              fontWeight: 600, fontSize: '0.78rem',
              cursor: index === total - 1 ? 'not-allowed' : 'pointer',
            }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Subject Accordion ─────────────────────────────────────────
function SubjectAccordion({ subject, chapters, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [activeIdx, setActiveIdx] = useState(null);
  const s = getSubjectStyle(subject);

  return (
    <div style={{
      border: `1px solid ${s.border}`,
      borderRadius: 12, overflow: 'hidden', marginBottom: '0.8rem',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: '0.9rem', padding: '1rem 1.3rem',
          background: open ? s.bg : 'var(--white)',
          border: 'none', cursor: 'pointer',
          transition: 'background 0.2s', textAlign: 'left',
        }}
      >
        <span style={{
          width: 38, height: 38, borderRadius: 10,
          background: s.accent, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.1rem', flexShrink: 0,
        }}>{SUBJECT_ICONS[subject] || <FaBook size={18} />}</span>

        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: s.accent, fontSize: '1rem' }}>{subject}</div>
          <div style={{ color: 'var(--gray)', fontSize: '0.8rem', marginTop: '0.1rem' }}>
            {chapters.length} chapter{chapters.length !== 1 ? 's' : ''}
          </div>
        </div>

        <span style={{
          background: s.accent, color: '#fff',
          padding: '0.2rem 0.7rem', borderRadius: 20,
          fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
        }}>{chapters.length}</span>

        <span style={{
          color: s.accent, fontSize: '0.9rem', flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.25s',
        }}><FaChevronDown size={14} /></span>
      </button>

      {open && (
        <div style={{
          padding: '0.8rem 1rem 1rem', background: s.bg,
          display: 'flex', flexDirection: 'column', gap: '0.5rem',
        }}>
          {chapters.map((ch, idx) => (
            <ChapterRow
              key={ch.id ?? idx}
              chapter={ch}
              index={idx}
              total={chapters.length}
              isActive={activeIdx === idx}
              onActivate={() => setActiveIdx(activeIdx === idx ? null : idx)}
              onPrev={() => setActiveIdx(idx - 1)}
              onNext={() => setActiveIdx(idx + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Class Accordion ───────────────────────────────────────────
function ClassAccordion({ classData, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const totalChapters = classData.subjects.reduce((s, sub) => s + sub.chapters.length, 0);

  return (
    <div style={{
      border: '2px solid var(--border)', borderRadius: 16,
      overflow: 'hidden', marginBottom: '1.2rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: '1rem', padding: '1.2rem 1.5rem',
          background: open
            ? 'linear-gradient(135deg, var(--blue) 0%, #1d4ed8 100%)'
            : 'var(--white)',
          border: 'none', cursor: 'pointer',
          transition: 'background 0.25s', textAlign: 'left',
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: open ? 'rgba(255,255,255,0.2)' : 'var(--blue-pale)',
          border: `2px solid ${open ? 'rgba(255,255,255,0.3)' : '#BFDBFE'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.4rem', flexShrink: 0, color: open ? '#fff' : 'var(--blue)',
        }}><FaGraduationCap size={24} /></div>

        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 800, fontSize: '1.15rem',
            color: open ? '#fff' : 'var(--dark)',
          }}>{classData.className}</div>
          <div style={{
            fontSize: '0.82rem', marginTop: '0.2rem',
            color: open ? 'rgba(255,255,255,0.75)' : 'var(--gray)',
          }}>
            {classData.subjects.length} subject{classData.subjects.length !== 1 ? 's' : ''} &bull; {totalChapters} chapter{totalChapters !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Subject pills */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {classData.subjects.map(sub => {
            const st = getSubjectStyle(sub.subject);
            return (
              <span key={sub.subject} style={{
                background: open ? 'rgba(255,255,255,0.15)' : st.bg,
                color: open ? '#fff' : st.accent,
                border: `1px solid ${open ? 'rgba(255,255,255,0.25)' : st.border}`,
                padding: '0.2rem 0.6rem', borderRadius: 20,
                fontSize: '0.72rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem',
              }}>{SUBJECT_ICONS[sub.subject] || <FaBook size={10} />} {sub.subject}</span>
            );
          })}
        </div>

        <span style={{
          color: open ? '#fff' : 'var(--blue)', fontSize: '0.9rem', flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.25s',
        }}><FaChevronDown size={14} /></span>
      </button>

      {open && (
        <div style={{ padding: '1rem 1.2rem 1.2rem', background: 'var(--bg)' }}>
          {classData.subjects.map((sub, i) => (
            <SubjectAccordion
              key={sub.subject}
              subject={sub.subject}
              chapters={sub.chapters}
              defaultOpen={i === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{
      textAlign: 'center', padding: '4rem 2rem',
      background: 'var(--white)', border: '1px solid var(--border)',
      borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    }}>
      <div style={{ fontSize: '3.5rem', marginBottom: '1rem', display: 'flex', justifyContent: 'center', color: 'var(--gray)' }}><FaInbox size={56} /></div>
      <h3 style={{ color: 'var(--dark)', fontWeight: 700, marginBottom: '0.5rem' }}>
        No Notes Available Yet
      </h3>
      <p style={{ color: 'var(--gray)', fontSize: '0.95rem', maxWidth: 380, margin: '0 auto' }}>
        Study materials will appear here once the admin uploads them. Check back soon!
      </p>
    </div>
  );
}

// ── Main Notes Page ───────────────────────────────────────────
export default function Notes() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getStructuredNotes()
      .then(r => setData(r.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // Flat search across all classes → subjects → chapters
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    const results = [];
    data.forEach(cls => {
      cls.subjects.forEach(sub => {
        sub.chapters.forEach((ch, idx) => {
          if (
            ch.title.toLowerCase().includes(q) ||
            sub.subject.toLowerCase().includes(q) ||
            cls.className.toLowerCase().includes(q)
          ) {
            results.push({ ...ch, _className: cls.className, _subject: sub.subject, _idx: idx });
          }
        });
      });
    });
    return results;
  }, [search, data]);

  const totalClasses  = data.length;
  const totalSubjects = data.reduce((s, c) => s + c.subjects.length, 0);
  const totalChapters = data.reduce((s, c) => s + c.subjects.reduce((ss, sub) => ss + sub.chapters.length, 0), 0);

  return (
    <div className="page">
      {/* Header */}
      <div className="page-title">
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          background: '#F0FDF4', border: '1px solid #BBF7D0',
          color: 'var(--green-dark)', padding: '0.3rem 1rem',
          borderRadius: 50, fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.8rem',
        }}>
          <FaUnlock size={12} /> Access Granted
        </div>
        <h2>Study <span>Notes</span></h2>
        <p>Browse chapters by class and subject — click to expand</p>
        <div className="accent-line" />
      </div>

      {/* Stats bar — only shown when data exists */}
      {!loading && !error && totalChapters > 0 && (
        <div style={{
          display: 'flex', gap: '1rem', justifyContent: 'center',
          flexWrap: 'wrap', margin: '1.5rem 0 2rem',
        }}>
          {[
            { label: 'Classes',  value: totalClasses,  icon: <FaGraduationCap size={20} /> },
            { label: 'Subjects', value: totalSubjects, icon: <FaBook size={20} /> },
            { label: 'Chapters', value: totalChapters, icon: <FaBook size={20} /> },
          ].map(({ label, value, icon }) => (
            <div key={label} style={{
              background: 'var(--white)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '0.7rem 1.5rem',
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}>
              <span style={{ fontSize: '1.2rem', color: 'var(--blue)' }}>{icon}</span>
              <div>
                <div style={{ fontWeight: 800, color: 'var(--blue)', fontSize: '1.2rem', lineHeight: 1 }}>{value}</div>
                <div style={{ color: 'var(--gray)', fontSize: '0.75rem' }}>{label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Search — only shown when data exists */}
      {!loading && !error && totalChapters > 0 && (
        <div style={{ maxWidth: 520, margin: '0 auto 2rem' }}>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: '1rem', top: '50%',
              transform: 'translateY(-50%)', fontSize: '1rem', pointerEvents: 'none', color: 'var(--gray)',
            }}><FaSearch size={14} /></span>
            <input
              type="text"
              placeholder="Search chapters, subjects or class..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', background: 'var(--white)',
                border: '1.5px solid var(--border)', borderRadius: 8,
                padding: '0.85rem 2.8rem 0.85rem 2.8rem',
                color: 'var(--dark)', fontSize: '0.95rem', fontFamily: 'inherit',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--blue-mid)'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                position: 'absolute', right: '0.8rem', top: '50%',
                transform: 'translateY(-50%)', background: 'none',
                border: 'none', cursor: 'pointer', color: 'var(--gray)', fontSize: '1rem',
              }}><FaTimes size={14} /></button>
            )}
          </div>
        </div>
      )}

      {/* ── Content area ── */}
      {loading ? (
        <div className="loading">
          <div className="spinner" />
          <p>Loading notes...</p>
        </div>

      ) : error ? (
        <div className="alert alert-error" style={{ maxWidth: 500, margin: '2rem auto' }}>
          ⚠️ Could not load notes. Please check your connection and try again.
        </div>

      ) : searchResults !== null ? (
        /* Search results */
        <div>
          <div style={{ color: 'var(--gray)', fontSize: '0.88rem', marginBottom: '1rem' }}>
            {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"
          </div>
          {searchResults.length === 0 ? (
            <div className="loading"><p>No chapters found for "{search}"</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {searchResults.map((ch, i) => {
                const st = getSubjectStyle(ch._subject);
                const url = ch.fileUrl && ch.fileUrl !== '#'
                  ? (ch.fileUrl.startsWith('http') ? ch.fileUrl : `https://sbs-coaching-backend-production.up.railway.app${ch.fileUrl}`)
                  : null;
                return (
                  <div key={i} style={{
                    background: 'var(--white)', border: `1px solid ${st.border}`,
                    borderRadius: 10, padding: '0.9rem 1.2rem',
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: '50%',
                      background: st.accent, color: '#fff',
                      fontWeight: 800, fontSize: '0.78rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>{ch.chapterNumber ?? ch._idx + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: 'var(--dark)', fontSize: '0.92rem' }}>
                        Chapter {ch.chapterNumber ?? ch._idx + 1}: {ch.title}
                      </div>
                      <div style={{ color: 'var(--gray)', fontSize: '0.78rem', marginTop: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        {ch._className} &bull; {SUBJECT_ICONS[ch._subject] || <FaBook size={10} />} {ch._subject}
                      </div>
                    </div>
                    {url ? (
                      <a href={url} download style={{
                        background: 'var(--blue)', color: '#fff',
                        padding: '0.4rem 1rem', borderRadius: 6,
                        fontWeight: 600, fontSize: '0.8rem',
                        textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.3rem',
                      }}><FaDownload size={12} /> Download</a>
                    ) : (
                      <span style={{
                        background: 'var(--gray-light)', color: 'var(--gray)',
                        padding: '0.4rem 1rem', borderRadius: 6,
                        fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0,
                      }}>Coming Soon</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      ) : totalChapters === 0 ? (
        <EmptyState />

      ) : (
        /* Accordion tree — fully dynamic from API */
        data.map((cls, i) => (
          <ClassAccordion
            key={cls.className}
            classData={cls}
            defaultOpen={i === 0}
          />
        ))
      )}

      {/* Security notice */}
      {!loading && (
        <div style={{
          marginTop: '3rem', background: 'var(--blue-pale)',
          border: '1px solid #BFDBFE', borderRadius: 12,
          padding: '1.2rem 1.5rem', display: 'flex',
          alignItems: 'flex-start', gap: '0.8rem',
        }}>
          <span style={{ fontSize: '1.3rem', flexShrink: 0, color: 'var(--blue)' }}><FaLock size={20} /></span>
          <div style={{ fontSize: '0.88rem', color: 'var(--blue)', lineHeight: 1.6 }}>
            <strong>Secure Access:</strong> These notes are available only to enrolled students with valid access codes. Please do not share your code with others.
          </div>
        </div>
      )}
    </div>
  );
}
